import * as React from 'react'
// @ts-expect-error - react-dom is an optional peer dependency
import * as ReactDOM from 'react-dom/client'

import type { RenderCallbackContent } from '../types/cell-content'

/**
 * Options for creating a React cell
 */
export interface ReactCellOptions {
  /**
   * React component to render
   * Uses any to support different React type versions across projects
   */
  component: any

  /**
   * Props to pass to the component
   */
  props?: Record<string, any>

  /**
   * Event listeners to attach to the component
   * @example
   * {
   *   click: (e) => console.log('clicked', e),
   *   change: (value) => console.log('changed', value)
   * }
   */
  on?: Record<string, (...args: any[]) => void>

  /**
   * Children to pass to the component
   */
  children?: any
}

/**
 * Create a datatable cell that renders a React component
 *
 * This helper manages the React component lifecycle, mounting and unmounting
 * the component when the cell is added/removed from the table.
 *
 * @param options - React cell options
 * @returns Render callback content for the datatable
 *
 * @example
 * ```typescript
 * import { createReactCell } from 'dap-design-system/helpers/react'
 * import StatusBadge from './StatusBadge'
 *
 * const columns = [
 *   {
 *     id: 'status',
 *     header: 'Status',
 *     cell: (row) => createReactCell({
 *       component: StatusBadge,
 *       props: {
 *         status: row.status,
 *         variant: 'success'
 *       },
 *       on: {
 *         click: () => console.log('Badge clicked')
 *       }
 *     })
 *   }
 * ]
 * ```
 */
export function createReactCell(
  options: ReactCellOptions,
): RenderCallbackContent
export function createReactCell(
  component: any,
  props?: Record<string, any>,
): RenderCallbackContent

export function createReactCell(
  componentOrOptions: any | ReactCellOptions,
  propsArg?: Record<string, any>,
): RenderCallbackContent {
  // Normalize arguments - check if first arg is ReactCellOptions
  const isOptions = (arg: any | ReactCellOptions): arg is ReactCellOptions => {
    return (
      typeof arg === 'object' &&
      arg !== null &&
      'component' in arg &&
      typeof arg.component !== 'undefined'
    )
  }

  const options: ReactCellOptions = isOptions(componentOrOptions)
    ? componentOrOptions
    : {
        component: componentOrOptions,
        props: propsArg,
      }

  const { component, props, on, children } = options

  // Track the React root instance
  let root: ReactDOM.Root | null = null

  // Error Boundary component to catch React rendering errors
  class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean }
  > {
    constructor(props: { children: React.ReactNode }) {
      super(props)
      this.state = { hasError: false }
    }

    static getDerivedStateFromError() {
      return { hasError: true }
    }

    componentDidCatch(error: Error) {
      console.error('Error rendering React component in datatable cell:', error)
    }

    render() {
      if (this.state.hasError) {
        return 'Error rendering cell'
      }
      return this.props.children
    }
  }

  return {
    render: (container: HTMLElement) => {
      // Create React root and render component
      try {
        // Build event handlers object
        const eventHandlers: Record<string, any> = {}
        if (on) {
          Object.entries(on).forEach(([event, handler]) => {
            // React expects event handlers in the format: onEventName
            const eventKey = `on${event.charAt(0).toUpperCase()}${event.slice(1)}`
            eventHandlers[eventKey] = handler
          })
        }

        // Create React element with props, events, and children
        const element = React.createElement(
          component,
          {
            ...props,
            ...eventHandlers,
          },
          children,
        )

        // Wrap in error boundary
        const wrappedElement = React.createElement(ErrorBoundary, null, element)

        // Create root and render
        root = ReactDOM.createRoot(container)
        root.render(wrappedElement)
      } catch (error) {
        console.error(
          'Error rendering React component in datatable cell:',
          error,
        )
        container.textContent = 'Error rendering cell'
      }
    },

    cleanup: () => {
      // Unmount the React component
      if (root !== null) {
        try {
          root.unmount()
        } catch (error) {
          console.error(
            'Error unmounting React component in datatable cell:',
            error,
          )
        } finally {
          root = null
        }
      }
    },
  }
}

/**
 * Type helper for creating type-safe cell renderers
 *
 * @example
 * ```typescript
 * import type { ReactCellRenderer } from 'dap-design-system/helpers/react'
 *
 * interface User {
 *   id: number
 *   status: string
 * }
 *
 * const statusCell: ReactCellRenderer<User> = (row) => createReactCell({
 *   component: StatusBadge,
 *   props: { status: row.status }
 * })
 * ```
 */
export type ReactCellRenderer<TData = any> = (
  row: TData,
) => RenderCallbackContent
